import { Box, Flex, HStack, Stack, Text } from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import { DtType, PerspectiveBaseInfoFragment } from '../../generated/graphql';
import { HelivanColor } from '../helivan/HelivanColor';
import { HelivanText } from '../helivan/HelivanText';
import { IoSparklesOutline, IoChevronForward } from 'react-icons/io5';

// Helper to get entity name from perspective
function getEntityName(perspective: PerspectiveBaseInfoFragment): string {
  return perspective.digitalTwin?.name ?? perspective.customAgent?.name ?? 'Unknown';
}

interface SubtopicDemographicInsightsProps {
  perspectives: PerspectiveBaseInfoFragment[];
  leftTitle?: string;
  rightTitle?: string;
  digitalTwinType: DtType;
}

interface DemographicStats {
  category: string;
  value: string;
  averageX: number;
  count: number;
  percentage: number;
}

interface OutlierInfo {
  name: string;
  x: number;
  demographics: string[];
}

export const SubtopicDemographicInsights = ({
  perspectives,
  leftTitle,
  rightTitle,
  digitalTwinType,
}: SubtopicDemographicInsightsProps) => {
  // State for collapsible insights
  const [isExpanded, setIsExpanded] = useState(false);

  // Calculate demographic statistics
  const { leftPreferences, rightPreferences, outliers } = useMemo(() => {
    // Group perspectives by demographic attributes
    const demographicGroups = new Map<string, number[]>();
    const demographicCounts = new Map<string, number>();

    perspectives.forEach((perspective) => {
      const demographics = extractDemographics(perspective, digitalTwinType);

      demographics.forEach((demo) => {
        const key = `${demo.category}: ${demo.value}`;
        if (!demographicGroups.has(key)) {
          demographicGroups.set(key, []);
          demographicCounts.set(key, 0);
        }
        demographicGroups.get(key)!.push(perspective.x);
        demographicCounts.set(key, demographicCounts.get(key)! + 1);
      });
    });

    // Calculate average x for each demographic
    const demographicStats: DemographicStats[] = [];
    demographicGroups.forEach((xValues, key) => {
      const [category, value] = key.split(': ');
      const averageX = xValues.reduce((sum, x) => sum + x, 0) / xValues.length;
      const count = demographicCounts.get(key)!;
      const percentage = (count / perspectives.length) * 100;

      demographicStats.push({
        category,
        value,
        averageX,
        count,
        percentage,
      });
    });

    // Sort by average X to find left and right preferences
    demographicStats.sort((a, b) => a.averageX - b.averageX);

    // Get top 3 left preferences (most negative x)
    const leftPrefs = demographicStats
      .filter((stat) => stat.averageX < 0 && stat.count >= 3) // At least 3 people
      .slice(0, 3);

    // Get top 3 right preferences (most positive x)
    const rightPrefs = demographicStats
      .filter((stat) => stat.averageX > 0 && stat.count >= 3) // At least 3 people
      .reverse()
      .slice(0, 3);

    // Find outliers (using IQR method)
    const xValues = perspectives.map((p) => p.x).sort((a, b) => a - b);
    const q1Index = Math.floor(xValues.length * 0.25);
    const q3Index = Math.floor(xValues.length * 0.75);
    const q1 = xValues[q1Index];
    const q3 = xValues[q3Index];
    const iqr = q3 - q1;
    const lowerBound = q1 - 1.5 * iqr;
    const upperBound = q3 + 1.5 * iqr;

    const outlierPerspectives = perspectives
      .filter((p) => p.x < lowerBound || p.x > upperBound)
      .map((p) => ({
        name: getEntityName(p),
        x: p.x,
        demographics: extractDemographics(p, digitalTwinType).map(
          (d) => `${d.category}: ${d.value}`
        ),
      }))
      .sort((a, b) => Math.abs(b.x) - Math.abs(a.x)) // Sort by distance from center
      .slice(0, 5); // Top 5 outliers

    return {
      leftPreferences: leftPrefs,
      rightPreferences: rightPrefs,
      outliers: outlierPerspectives,
    };
  }, [perspectives, digitalTwinType]);

  if (perspectives.length < 10) {
    return null; // Don't show insights if we don't have enough data
  }

  return (
    <Stack spacing={2} width="full" px={4} py={3}>
      <HStack
        spacing={2}
        cursor="pointer"
        onClick={() => setIsExpanded(!isExpanded)}
        userSelect="none"
        alignItems="center"
      >
        <Box
          style={{
            transition: 'transform 0.2s',
            transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <IoChevronForward color={HelivanColor.text.blueMid} size={16} />
        </Box>
        <IoSparklesOutline color={HelivanColor.text.helivanBlue} size={20} />
        <HelivanText
          variant="largeBody+"
          colorOverride={HelivanColor.text.helivanBlue}
        >
          Insights
        </HelivanText>
      </HStack>

      {isExpanded && (
        <Flex gap={6} width="full" flexWrap="wrap">
          {/* Left preferences */}
          {leftTitle && leftPreferences.length > 0 && (
            <Box flex="1" minWidth="250px">
              <HelivanText variant="smallBody+" mb={2}>
                Prefer "{leftTitle}"
              </HelivanText>
              <Stack spacing={1}>
                {leftPreferences.map((pref, idx) => (
                  <Flex key={idx} alignItems="center" gap={2}>
                    <Box
                      width="8px"
                      height="8px"
                      borderRadius="full"
                      bg={HelivanColor.background.primary}
                    />
                    <HelivanText variant="smallBody" colorOverride="black">
                      {pref.value}{' '}
                      <Text as="span" color="gray.500">
                        ({pref.count} people, avg: {pref.averageX.toFixed(2)})
                      </Text>
                    </HelivanText>
                  </Flex>
                ))}
              </Stack>
            </Box>
          )}

          {/* Right preferences */}
          {rightTitle && rightPreferences.length > 0 && (
            <Box flex="1" minWidth="250px">
              <HelivanText variant="smallBody+" mb={2}>
                Prefer "{rightTitle}"
              </HelivanText>
              <Stack spacing={1}>
                {rightPreferences.map((pref, idx) => (
                  <Flex key={idx} alignItems="center" gap={2}>
                    <Box
                      width="8px"
                      height="8px"
                      borderRadius="full"
                      bg={HelivanColor.background.primary}
                    />
                    <HelivanText variant="smallBody" colorOverride="black">
                      {pref.value}{' '}
                      <Text as="span" color="gray.500">
                        ({pref.count} people, avg: {pref.averageX.toFixed(2)})
                      </Text>
                    </HelivanText>
                  </Flex>
                ))}
              </Stack>
            </Box>
          )}

          {/* Outliers */}
          {outliers.length > 0 && (
            <Box flex="1" minWidth="250px">
              <HelivanText variant="smallBody+" mb={2}>
                Notable Outliers
              </HelivanText>
              <Stack spacing={1}>
                {outliers.map((outlier, idx) => (
                  <Flex key={idx} alignItems="flex-start" gap={2}>
                    <Box
                      width="8px"
                      height="8px"
                      borderRadius="full"
                      bg="red.500"
                      mt={1}
                    />
                    <Box>
                      <HelivanText variant="smallBody" colorOverride="black">
                        {outlier.name}{' '}
                        <Text as="span" color="gray.500">
                          (x: {outlier.x.toFixed(2)})
                        </Text>
                      </HelivanText>
                      {outlier.demographics.length > 0 && (
                        <HelivanText
                          variant="tinyBody"
                          colorOverride="gray.600"
                        >
                          {outlier.demographics.slice(0, 2).join(', ')}
                        </HelivanText>
                      )}
                    </Box>
                  </Flex>
                ))}
              </Stack>
            </Box>
          )}
        </Flex>
      )}

      {isExpanded &&
        leftPreferences.length === 0 &&
        rightPreferences.length === 0 &&
        outliers.length === 0 && (
          <HelivanText variant="smallBody" colorOverride="gray.500">
            Not enough variation in the data to show meaningful demographic
            insights.
          </HelivanText>
        )}
    </Stack>
  );
};

// Helper function to extract demographics from a perspective based on digitalTwinType
function extractDemographics(
  perspective: PerspectiveBaseInfoFragment,
  digitalTwinType: DtType
): Array<{ category: string; value: string }> {
  const demographics: Array<{ category: string; value: string }> = [];
  const dt = perspective.digitalTwin;

  // Custom agents don't have demographics
  if (!dt) return demographics;

  switch (digitalTwinType) {
    case DtType.Congressperson:
      if (dt.congressData) {
        const data = dt.congressData;
        demographics.push(
          { category: 'Party', value: data.party },
          { category: 'Chamber', value: data.chamber },
          { category: 'State', value: data.state }
        );
      }
      break;

    case DtType.EarlyStageVcs:
      if (dt.earlyStageVcData) {
        const data = dt.earlyStageVcData;
        if (data.firm)
          demographics.push({ category: 'Firm', value: data.firm });
        if (data.title)
          demographics.push({ category: 'Title', value: data.title });
        if (data.location)
          demographics.push({ category: 'Location', value: data.location });
      }
      break;

    case DtType.FortuneCeos:
      if (dt.fortuneCeoData) {
        const data = dt.fortuneCeoData;
        demographics.push(
          { category: 'Company', value: data.company },
          { category: 'Industry', value: data.industry }
        );
      }
      break;

    case DtType.MoviesSimple:
      if (dt.moviesSimpleData) {
        const data = dt.moviesSimpleData;
        demographics.push(
          { category: 'Age', value: data.age },
          { category: 'Gender', value: data.gender }
        );
      }
      break;
    case DtType.Model:
      if (dt.modelData) {
        const data = dt.modelData;
        demographics.push(
          { category: 'Model Name', value: data.name ?? '' },
          { category: 'Score', value: data.score?.toString() ?? '' },
          { category: 'Base Model', value: data.baseModel ?? '' },
          { category: 'System Prompt', value: data.systemPrompt ?? '' },
          {
            category: 'Temperature',
            value: data.temperature?.toString() ?? '',
          },
          {
            category: 'Max New Tokens',
            value: data.maxNewTokens?.toString() ?? '',
          },
          {
            category: 'Num Return Sequences',
            value: data.numReturnSequences?.toString() ?? '',
          }
        );
      }
      break;

    case DtType.MoviesFull:
    case DtType.MoviesMedium:
      if (dt.moviesMediumFullData) {
        const data = dt.moviesMediumFullData;
        demographics.push(
          { category: 'Age', value: data.age },
          { category: 'Gender', value: data.gender },
          { category: 'Race', value: data.race },
          { category: 'Region', value: data.region },
          { category: 'Education', value: data.education },
          { category: 'Employment', value: data.employment },
          { category: 'Income', value: data.income },
          { category: 'Location Type', value: data.locationType }
        );
      }
      break;

    default:
      break;
  }

  return demographics;
}
